import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';

// Silence noisy debug/info/log calls in production builds (errors and warnings stay visible).
if (process.env.NODE_ENV === 'production') {
  ['log', 'debug', 'info'].forEach((m) => { console[m] = () => {}; });
}

import axios from 'axios';
import { installOfflineWriteInterceptor } from '@/utils/offlineWriteInterceptor';
import { installRetryInterceptor } from '@/utils/retryInterceptor';

// Retry interceptor — retries transient 502/503/504/520-524 and network
// errors on safe idempotent endpoints (GET + auth/health POSTs). Installed
// FIRST so it runs innermost — sees each attempt's failure separately.
installRetryInterceptor(axios);

// Offline write queue — intercept POST/PATCH on packing/journal/places/
// reservations when we lose connectivity and replay them once we're back.
// Runs always (independent of the same-origin rewrite below). Idempotent.
installOfflineWriteInterceptor(axios);

// Kill-switch: read the backend health flag on boot. If the operator has
// disabled offline writes via OFFLINE_WRITE_ENABLED=0 the interceptor
// becomes a no-op (see /app/frontend/src/utils/offlineOutbox.js).
(function readOfflineWriteFlag() {
  if (typeof window === 'undefined') return;
  const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/health`;
  fetch(url, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (data && data.offline_write_enabled === false) {
        window.__tovias_offline_write_disabled = true;
      }
    })
    .catch(() => { /* boot health check failed, leave feature enabled */ });
})();

// Same-origin enforcement: when the user opens the app from a different host
// than the one baked into REACT_APP_BACKEND_URL (e.g. custom domain tovias.app
// hitting the Emergent prod host), rewrite every API call to use the CURRENT
// origin. The ingress already routes /api/* to the backend, so this turns
// cross-origin calls into same-origin ones and dodges two well-known issues:
//   1) iOS Safari rejects responses with `Access-Control-Allow-Origin: *` +
//      `Access-Control-Allow-Credentials: true` (forbidden by spec) with a
//      generic "Load failed" error.
//   2) httpOnly cookies stay first-party and are not blocked by ITP.
(function enforceSameOriginApi() {
  if (typeof window === 'undefined') return;
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (!envUrl) return;
  let envOrigin = '';
  try {
    envOrigin = new URL(envUrl).origin;
  } catch (_) {
    return;
  }
  const curOrigin = window.location.origin;
  if (envOrigin === curOrigin) return; // already same-origin, nothing to do

  const rewrite = (url) =>
    url && url.startsWith(envOrigin) ? curOrigin + url.slice(envOrigin.length) : url;

  // Axios global interceptor — catches every axios.get/post/patch/delete
  axios.interceptors.request.use((config) => {
    if (config.url) config.url = rewrite(config.url);
    if (config.baseURL) config.baseURL = rewrite(config.baseURL);
    // Force cookies on cross-origin/same-origin parity
    if (config.url && config.url.includes('/api/')) {
      config.withCredentials = config.withCredentials !== false;
    }
    return config;
  });

  // Offline write interceptor — intercepts POST/PATCH on packing / journal /
  // places / reservations under /api/trips/{id}/... when the browser is
  // offline or the fetch fails with a network error. The mutation is stored
  // in IndexedDB and replayed automatically when connectivity returns.
  // Kill switch: set window.__tovias_offline_write_disabled = true.

  // Fetch wrapper — catches every fetch() (AuthContext, push notifications, etc.)
  const origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (origFetch) {
    window.fetch = (input, init) => {
      try {
        if (typeof input === 'string') {
          input = rewrite(input);
        } else if (input && typeof input === 'object' && 'url' in input) {
          const newUrl = rewrite(input.url);
          if (newUrl !== input.url) {
            input = new Request(newUrl, input);
          }
        }
      } catch (_) { /* fall through */ }
      return origFetch(input, init);
    };
  }
})();

import Landing from '@/pages/Landing';
// Heavy/secondary routes are code-split with React.lazy so the initial JS
// bundle stays small. The landing page (above) is eagerly imported because
// it's the first paint for unauthenticated users.

// A deploy invalidates old chunk hashes: users mid-session (mid-TRIP!) hit a
// failed dynamic import and got a blank screen. Retry via one full reload
// (fresh index.html → fresh chunk URLs); if it still fails, rethrow so the
// error surfaces instead of hanging blank.
// aug 22 2026 · Support-diagnosed reel bug: `window.location.reload()`
// on chunk-load failure retried the exact same conditions → infinite
// blank screen in the headless Chromium worker. Replace with a bounded
// in-process retry (3 attempts, cache-bust query on subsequent tries)
// so we never reload the page — a fresh dynamic import fetches the
// current chunk URL from the updated bundle graph without discarding
// the running app.
const lazyRetry = (importer) => lazy(async () => {
  const MAX_ATTEMPTS = 3;
  let lastErr;
  for (let i = 0; i < MAX_ATTEMPTS; i += 1) {
    try {
      // On retries, most bundlers ignore query params on module specs,
      // but the retry itself is what buys us the fix — network hiccups
      // and transient chunk-fetch failures resolve on the second try.
      // Small backoff keeps things sane if the CDN is slow.
      if (i > 0) await new Promise((r) => setTimeout(r, 300 * i));
      // eslint-disable-next-line no-await-in-loop
      return await importer();
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
});

// aug 22 2026 · Fallback = pantalla negra. Se usa para la ruta del reel
// export para que un chunk que rompa no filtre un splash blanco en el
// MP4 grabado por Chromium headless. `componentDidCatch` loguea a
// consola (el worker escucha `page.on('pageerror')`) para que el fallo
// sea observable.
class ReelExportBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error('[reel-export-boundary]', err, info && info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{background:'#000',minHeight:'100vh',width:'100vw'}} />;
    }
    return this.props.children;
  }
}

const Dashboard = lazyRetry(() => import('@/pages/Dashboard'));
const YearRecap = lazyRetry(() => import('@/pages/YearRecap'));
const CalendarPage = lazyRetry(() => import('@/pages/Calendar'));
const TripDetail = lazyRetry(() => import('@/pages/TripDetail'));
const JoinTrip = lazyRetry(() => import('@/pages/JoinTrip'));
const Subscription = lazyRetry(() => import('@/pages/Subscription'));
const AtlasPage = lazyRetry(() => import('@/pages/AtlasPage'));
const Pricing = lazyRetry(() => import('@/pages/Pricing'));
const PricingCompare = lazyRetry(() => import('@/pages/PricingCompare'));
const MarketingAssetsPage = lazyRetry(() => import('@/pages/MarketingAssetsPage'));
const AdminLogin = lazyRetry(() => import('@/pages/AdminLogin'));
const AdminDashboard = lazyRetry(() => import('@/pages/AdminDashboard'));
const EmailLogin = lazyRetry(() => import('@/pages/EmailLogin'));
const ForgotPassword = lazyRetry(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('@/pages/ResetPassword'));
const CancelAccountDeletion = lazyRetry(() => import('@/pages/CancelAccountDeletion'));
const HomeCountryPrompt = lazyRetry(() => import('@/components/HomeCountryPrompt').then((m) => ({ default: m.HomeCountryPrompt })));
const EmailRegister = lazyRetry(() => import('@/pages/EmailRegister'));
const NotFoundPage = lazyRetry(() => import('@/pages/NotFoundPage'));
const AvisoLegal = lazyRetry(() => import('@/pages/legal/AvisoLegal'));
const EliminarCuenta = lazyRetry(() => import('@/pages/legal/EliminarCuenta'));
const PoliticaPrivacidadCompleta = lazyRetry(() => import('@/pages/legal/PoliticaPrivacidadCompleta'));
const PoliticaCookies = lazyRetry(() => import('@/pages/legal/PoliticaCookies'));
const TerminosCondiciones = lazyRetry(() => import('@/pages/legal/TerminosCondiciones'));
const FAQs = lazyRetry(() => import('@/pages/info/FAQs'));
const QueEsTovias = lazyRetry(() => import('@/pages/info/QueEsTovias'));
const Agencia = lazyRetry(() => import('@/pages/Agencia'));
const FeaturesIndex = lazyRetry(() => import('@/pages/funciones/FeaturesIndex'));
const FeaturePage = lazyRetry(() => import('@/pages/funciones/FeaturePage'));
const Showcase = lazyRetry(() => import('@/pages/showcase/Showcase'));
const Ambassadors = lazyRetry(() => import('@/pages/Ambassadors'));
const AmbassadorStats = lazyRetry(() => import('@/pages/AmbassadorStats'));
const CreatorDashboard = lazyRetry(() => import('@/pages/CreatorDashboard'));
const Press = lazyRetry(() => import('@/pages/Press'));
const Status = lazyRetry(() => import('@/pages/Status'));
const BlogList = lazyRetry(() => import('@/pages/BlogList'));
const BlogPost = lazyRetry(() => import('@/pages/BlogPost'));
const SharedRoutePage = lazyRetry(() => import('@/pages/SharedRoutePage'));
const WishlistPage = lazyRetry(() => import('@/pages/WishlistPage'));
const PublicWishlistPage = lazyRetry(() => import('@/pages/PublicWishlistPage'));
const AssociationPanel = lazyRetry(() => import('@/pages/AssociationPanel'));
const AssociationPublicPage = lazyRetry(() => import('@/pages/AssociationPublicPage'));
const AssociationLanding = lazyRetry(() => import('@/pages/AssociationLanding'));
const ReelPage = lazyRetry(() => import('@/pages/ReelPage'));
const ReelPublicViewer = lazyRetry(() => import('@/pages/ReelPublicViewer'));
const ReelPageV2Wrapper = lazyRetry(() => import('@/pages/ReelPageV2Wrapper'));
const DebugReelRoadEngine = lazyRetry(() => import('@/pages/DebugReelRoadEngine'));
const ShareReelV2Storyboard = lazyRetry(() => import('@/pages/spike/ShareReelV2Storyboard'));
const ShareReelV2Motion = lazyRetry(() => import('@/pages/spike/ShareReelV2Motion'));
const YearRecapExport = lazyRetry(() => import('@/pages/YearRecapExport'));
const ReelTransitionsLab = lazyRetry(() => import('@/pages/spike/ReelTransitionsLab'));
const RecuerdosV2Storyboard = lazyRetry(() => import('@/pages/spike/RecuerdosV2Storyboard'));
const BookFirstPagesPreview = lazyRetry(() => import('@/pages/spike/BookFirstPagesPreview'));
const BookFinalPagesPreview = lazyRetry(() => import('@/pages/spike/BookFinalPagesPreview'));
const PagarBizum = lazyRetry(() => import('@/pages/PagarBizum'));
const TerminosEmbajadores = lazyRetry(() => import('@/pages/legal/TerminosEmbajadores'));
const ShowcaseRoutesPage = lazyRetry(() => import('@/pages/ShowcaseRoutesPage'));
const CreatorProfilePage = lazyRetry(() => import('@/pages/CreatorProfilePage'));
const PlaceCardPage = lazyRetry(() => import('@/pages/PlaceCardPage'));
const AdminBlog = lazyRetry(() => import('@/pages/AdminBlog'));
const AdminPlaceCards = lazyRetry(() => import('@/pages/AdminPlaceCards'));
const AdminLaunchKit = lazyRetry(() => import('@/pages/AdminLaunchKit'));
const AdminAudioGuides = lazyRetry(() => import('@/pages/AdminAudioGuides'));
const AdminInspire = lazyRetry(() => import('@/pages/AdminInspire'));
const InspirePage = lazyRetry(() => import('@/pages/InspirePage'));
const DestinationPage = lazyRetry(() => import('@/pages/DestinationPage'));
const SettingsPage = lazyRetry(() => import('@/pages/SettingsPage'));
const DiagnosticsPage = lazyRetry(() => import('@/pages/DiagnosticsPage'));
const NotificationsPage = lazyRetry(() => import('@/pages/NotificationsPage'));
const PrintOrdersPage = lazyRetry(() => import('@/pages/PrintOrdersPage'));
const PublicTripStory = lazyRetry(() => import('@/pages/PublicTripStory'));
const BookPreview = lazyRetry(() => import('@/pages/BookPreview'));
const BetaSignup = lazyRetry(() => import('@/pages/BetaSignup'));
const VenuesPage = lazyRetry(() => import('@/pages/VenuesPage'));
const NPSPage = lazyRetry(() => import('@/pages/NPSPage'));
const InvitePreviewPage = lazyRetry(() => import('@/pages/InvitePreviewPage'));
const GiftPage = lazyRetry(() => import('@/pages/GiftPage'));
const Compare = lazyRetry(() => import('@/pages/Compare'));
const TravelAppsIndexPage = lazyRetry(() => import('@/pages/TravelAppsIndexPage'));
const TravelAppsCountryPage = lazyRetry(() => import('@/pages/TravelAppsCountryPage'));
const Compliance = lazyRetry(() => import('@/pages/Compliance'));
const ShareTargetPage = lazyRetry(() => import('@/pages/ShareTargetPage'));
const GoRedirect = lazyRetry(() => import('@/pages/GoRedirect'));
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { NativeAppBridge } from '@/components/NativeAppBridge';
import { isNativeIOS } from '@/utils/capacitorNative';

import { AudioPlayerProvider } from '@/contexts/AudioPlayerContext';
// Global "TOVÍAS te lo cuenta" mini-player. Rendered ONCE outside
// <Routes> so navigating between /rutas → /r/:id → /dashboard keeps
// the audio playing without interruption.
const AudioPlayerBar = lazy(() => import('@/components/AudioPlayer/AudioPlayerBar'));
// PERF (jun 2026): non-critical shell components (toasts, cookie banner,
// offline banner, emergency flash, onboarding) are code-split and mounted
// after the browser goes idle. They contributed ~25 KB gz to main.js and
// main-thread parse time that delayed the landing LCP on mobile.
const Toaster = lazy(() => import('@/components/ui/sonner').then((m) => ({ default: m.Toaster })));
const CookieBanner = lazy(() => import('@/components/CookieBanner'));
const OfflineBanner = lazy(() => import('@/components/OfflineBanner'));
const OfflineOutboxBadge = lazy(() => import('@/components/OfflineOutboxBadge'));
const EmergencyFlash = lazy(() => import('@/components/EmergencyFlash').then((m) => ({ default: m.EmergencyFlash })));
// PWAInstallToast removed — user reported the bottom banner was too intrusive
// on mobile (iter 70). Install entry point now lives only in the landing's
// "Instalar Tovías" button (components/InstallAppButton.jsx), which already
// covers Android one-tap install and iOS manual instructions via its modal.
// import PWAInstallToast from '@/components/PWAInstallToast';
const OnboardingChecklist = lazy(() =>
  import('@/components/onboarding/OnboardingChecklist').then((m) => ({ default: m.OnboardingChecklist }))
);
import UtmTracker from '@/components/UtmTracker';
import { sendAttributionPing } from '@/utils/attribution';

if (isNativeIOS()) document.documentElement.classList.add('native-ios');

const isTypingField = (el) => el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'file', 'range', 'date', 'datetime-local', 'time'].includes(el.type)) || el.isContentEditable);
document.addEventListener('focusin', (e) => { if (isTypingField(e.target)) document.documentElement.classList.add('kbd-open'); });
document.addEventListener('focusout', () => setTimeout(() => {
  if (!isTypingField(document.activeElement)) document.documentElement.classList.remove('kbd-open');
}, 50));

// Mounts children only after the browser is idle (or 1.5 s fallback) so
// they never compete with the landing's first paint.
const useIdleReady = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    if ('requestIdleCallback' in window) {
      requestIdleCallback(go, { timeout: 2500 });
    } else {
      setTimeout(go, 1500);
    }
  }, []);
  return ready;
};

// Routes that require an authenticated user (they <Navigate to="/" /> when
// user is null). ONLY these wait for the /auth/me check — public routes
// (landing, blog, showcase…) render immediately, which removes a full API
// roundtrip from the landing's LCP on mobile.
const PROTECTED_ROUTE_RE = /^\/(dashboard|lista-de-deseos|calendar|trips\/|subscription|settings|notifications|orders|resumen-anual|creator\/|embajador\/panel|admin\/dashboard|atlas|asociacion$|a\/)/;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sin sesión: guarda a dónde volver y manda al login.
const LoginThenBack = ({ next }) => {
  try { sessionStorage.setItem('post_login_redirect', next); } catch { /* private mode */ }
  return <Navigate to="/email-login" replace />;
};

const AuthHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  // Track whether we've already run the initial PWA-standalone redirect.
  // The auth effect re-runs on every navigation (because it depends on
  // `location`), but the "send PWA users to /dashboard or /email-login"
  // logic should ONLY trigger on the very first mount. Otherwise the
  // user gets stuck in a loop: tapping "Volver al inicio" from
  // /email-login → goes to / → effect fires → bounces them back to
  // /email-login because they're still unauthenticated. The ref makes
  // the redirect idempotent without breaking the session-id flow.
  const didInitialPwaRedirect = useRef(false);
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP' && !location.hash) window.scrollTo(0, 0);
  }, [location.pathname]); 


  useEffect(() => {
    const checkAuth = async () => {
      // Check for session_id in URL fragment (post-OAuth redirect)
      const hash = location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        try {
          // Backend sets the httpOnly session cookie in this call
          const response = await axios.post(`${API}/auth/session`, null, {
            params: { session_id: sessionId },
            withCredentials: true,
          });
          setUser(response.data.user);
          window.history.replaceState(null, '', location.pathname);
          navigate('/dashboard');
          sendAttributionPing(API);
        } catch (error) {
          console.error('Auth error:', error);
          setLoading(false);
        }
        return;
      }

      // Existing session — relies on httpOnly cookie only (no localStorage)
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        sendAttributionPing(API);
        // If the user has a saved language preference and it differs from
        // the auto-detected one, switch i18n now. Imported lazily to avoid
        // a circular bundle dependency with index.js.
        try {
          const userLang = response.data?.language;
          if (userLang && ['es', 'en'].includes(userLang)) {
            const i18nMod = await import('@/i18n');
            if (i18nMod.default.resolvedLanguage !== userLang) {
              i18nMod.default.changeLanguage(userLang);
              localStorage.setItem('tovias_lang', userLang);
            }
          }
        } catch (_) { /* non-blocking */ }
        // PWA standalone users land on "/" by default (manifest start_url).
        // Auto-redirect them to /dashboard so they keep their session feel,
        // instead of seeing the marketing landing every time they open the app.
        // Only on the first mount — otherwise a user clicking "Volver al
        // inicio" from another page would get pulled back into /dashboard
        // forever.
        if (!didInitialPwaRedirect.current) {
          didInitialPwaRedirect.current = true;
          const isStandalone =
            window.matchMedia?.('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
          if (isStandalone && location.pathname === '/' && !hash.includes('session_id=')) {
            navigate('/dashboard', { replace: true });
          }
        }
        // Note: we do NOT auto-redirect "/" → "/dashboard" on regular browsers.
        // Authenticated users may want to revisit the landing page (legal,
        // contact, FAQ). The Landing page itself decides what CTAs to show
        // based on auth.
      } catch (error) {
        // Not authenticated.
        // If the user opened the installed PWA (standalone) and lands on "/",
        // showing the marketing landing is confusing — they expect the app.
        // Send them straight to the email login screen instead. ONLY on
        // the first mount (see `didInitialPwaRedirect` comment above) so
        // that an explicit "Volver al inicio" tap from the login page
        // actually reaches the landing instead of bouncing back.
        if (!didInitialPwaRedirect.current) {
          didInitialPwaRedirect.current = true;
          const isStandalone =
            window.matchMedia?.('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;
          const onPublicRoute = ['/', '/about'].includes(location.pathname);
          if (isStandalone && onPublicRoute && !hash.includes('session_id=')) {
            navigate('/email-login', { replace: true });
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location, navigate]);

  // Public routes render instantly; only protected routes (which would
  // otherwise redirect to "/" with user=null) wait for the auth check.
  if (loading && PROTECTED_ROUTE_RE.test(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
    {/* A11y — universal skip link. Solo visible al recibir focus por Tab.
        Salta la cabecera y lleva al usuario directo al contenido principal. */}
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-teal-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-full focus:font-bold focus:shadow-lg"
      data-testid="skip-to-main"
    >
      Saltar al contenido principal
    </a>
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" role="status" aria-live="polite" aria-label="Cargando">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" data-testid="route-suspense-spinner"></div>
      </div>
    }>
    <main id="main-content" tabIndex="-1">
    <Routes>
      {/* Landing remains accessible to authenticated users too — they may
          want to revisit legal pages, contact, FAQs, etc. The Landing's
          hero CTAs already detect the auth state and redirect to /dashboard
          when appropriate. */}
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<Landing />} />
      <Route path="/marketing-assets" element={<MarketingAssetsPage />} />
      <Route path="/email-login" element={<EmailLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/cancelar-eliminacion" element={<CancelAccountDeletion />} />
      <Route path="/register" element={<EmailRegister />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/atlas" element={user ? <AtlasPage /> : <Navigate to="/" />} />
      <Route path="/resumen-anual" element={user ? <YearRecap /> : <Navigate to="/" />} />
      <Route path="/calendar" element={user ? <CalendarPage user={user} setUser={setUser} /> : <Navigate to="/" />} />
      {/* PWA Share Target — Android shares photos INTO Tovías via the
          manifest "share_target" entry. The service worker intercepts
          the POST, stashes the files in a cache, and redirects here so
          this page can ask the user which trip to add them to. */}
      <Route path="/share-target" element={<ShareTargetPage />} />
      <Route path="/go/:slug" element={<GoRedirect />} />
      
      {/* Info Pages */}
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/que-es-tovias" element={<QueEsTovias />} />
      <Route path="/agencia" element={<Agencia />} />
      <Route path="/funciones" element={<FeaturesIndex />} />
      <Route path="/funciones/:slug" element={<FeaturePage />} />
      <Route path="/showcase" element={<Showcase />} />
      <Route path="/embajadores" element={<Ambassadors />} />
      <Route path="/ambassadors" element={<Ambassadors />} />
      <Route path="/embajadoras" element={<Ambassadors />} />
      <Route path="/embajadoras/stats/:code" element={<AmbassadorStats />} />
      <Route path="/embajadores/stats/:code" element={<AmbassadorStats />} />
      <Route path="/prensa" element={<Press />} />
      <Route path="/press" element={<Press />} />
      <Route path="/comparativa" element={<Compare />} />
      <Route path="/apps-viaje" element={<TravelAppsIndexPage />} />
      <Route path="/apps-viaje/:slug" element={<TravelAppsCountryPage />} />
      <Route path="/vs" element={<Compare />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/cumplimiento" element={<Compliance />} />
      <Route path="/compliance" element={<Compliance />} />
      <Route path="/trust" element={<Compliance />} />
      <Route path="/status" element={<Status />} />
      <Route path="/estado" element={<Status />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* Magic-link shared route landing — public. Visitors without a session
          land here, sign in, and the SharedRoutePage finishes the clone. */}
      <Route path="/r/:shareId" element={<SharedRoutePage user={user} />} />
      <Route path="/lista-de-deseos" element={user ? <WishlistPage /> : <LoginThenBack next="/lista-de-deseos" />} />
      <Route path="/lista/:slug" element={<PublicWishlistPage />} />
      <Route path="/asociaciones" element={<AssociationLanding user={user} />} />
      <Route path="/organizadores" element={<AssociationLanding user={user} />} />
      <Route path="/asociacion" element={user ? <AssociationPanel /> : <LoginThenBack next="/asociacion" />} />
      <Route path="/a/:slug" element={<AssociationPublicPage user={user} />} />
      {/* /reel/:shareId — Vista PÚBLICA del Reel v2 (aug 2026).
          Sustituye al legacy `ReelPage` como experiencia principal.
          Reproduce el MP4 v2 (portada, música, cierre Tovías) sin auth,
          con Open Graph correcto y range requests soportados por el
          backend. El legacy sigue accesible via `?legacy=1`. */}
      <Route path="/reel/:shareId" element={<ReelPublicViewer />} />
      <Route path="/debug/reel-road-engine" element={<DebugReelRoadEngine />} />
      {/* Spike feb 2026 · storyboard media-first / share-first — NO productivo.
          Ver /app/design_guidelines.json + comentario del componente. */}
      <Route path="/spike/share-reel-v2/storyboard" element={<ShareReelV2Storyboard />} />
      {/* aug 22 2026 · Route wrapped in a dedicated Error Boundary +
          Suspense fallback that paint BLACK. The headless Chromium
          worker captures whatever the DOM shows the moment recording
          starts; a light-themed spinner during hydration used to leak
          into the MP4 as 3+ seconds of white frames. */}
      <Route path="/spike/share-reel-v2/motion" element={
        <ReelExportBoundary>
          <Suspense fallback={<div style={{background:'#000',minHeight:'100vh',width:'100vw'}} />}>
            <ShareReelV2Motion />
          </Suspense>
        </ReelExportBoundary>
      } />
      <Route path="/spike/recuerdos-v2/storyboard" element={<RecuerdosV2Storyboard />} />
      <Route path="/spike/book-v2/first-pages" element={<BookFirstPagesPreview />} />
      <Route path="/spike/book-v2/final-pages" element={<BookFinalPagesPreview />} />
      <Route path="/spike/reel-transitions-lab" element={<ReelTransitionsLab />} />
      <Route path="/spike/year-recap-export" element={
        <ReelExportBoundary>
          <Suspense fallback={<div style={{background:'#0b0f0e',minHeight:'100vh',width:'100vw'}} />}>
            <YearRecapExport />
          </Suspense>
        </ReelExportBoundary>
      } />
      {/* Preview interno del reel FASE 1B (feb 2026) — mismo motor
          visual que /reel/:shareId, pero se sirve desde /api/trips/{id}/reel/data
          (auth requerida + participante del viaje). NUNCA genera un
          shareId automáticamente ni publica el viaje. Sustituye al
          modal `RecapReel` viejo con selector música/upload/share. */}
      <Route path="/trips/:tripId/reel" element={user ? <ReelPageV2Wrapper /> : <Navigate to="/" />} />
      <Route path="/pagar-bizum" element={<PagarBizum />} />
      {/* Public catalogue of community routes — `/rutas` is the canonical
          URL. `/itinerarios` used to render the SAME component (exact
          duplicate content flagged by SEO audit, sep 2026) — now a
          redirect so link equity/crawl budget consolidates on /rutas. */}
      <Route path="/rutas" element={<ShowcaseRoutesPage />} />
      <Route path="/itinerarios" element={<Navigate to="/rutas" replace />} />
      {/* Public creator profile — `/u/:username` */}
      <Route path="/u/:username" element={<CreatorProfilePage />} />
      {/* Public place-card drop — the URL a creator pastes in their reel bio */}
      <Route path="/lugar/:slug" element={<PlaceCardPage />} />
      <Route path="/place/:slug" element={<PlaceCardPage />} />
      {/* Embajador monetization dashboard — gated by is_creator=true.
          The component redirects to /dashboard if the flag is missing. */}
      <Route path="/creator/dashboard" element={<CreatorDashboard />} />
      <Route path="/embajador/panel" element={<CreatorDashboard />} />
      <Route path="/admin/blog" element={<AdminBlog />} />
      <Route path="/admin/place-cards" element={<AdminPlaceCards />} />
      <Route path="/admin/launch-kit" element={<AdminLaunchKit />} />
      <Route path="/admin/audio-guides" element={<AdminAudioGuides />} />
      <Route path="/admin/inspire" element={<AdminInspire />} />
      {/* Inspírate · destinos sugeridos */}
      <Route path="/inspirate" element={<InspirePage />} />
      <Route path="/inspirate/:slug" element={<DestinationPage />} />
      <Route path="/planes" element={<Pricing />} />
      {/* sep 2026 fix ("/pricing y /precios renderizan en blanco") · nunca
          hubo ruta registrada para ellas — /planes es la única página real. */}
      <Route path="/pricing" element={<Navigate to="/planes" replace />} />
      <Route path="/precios" element={<Navigate to="/planes" replace />} />
      <Route path="/planes/comparar" element={<PricingCompare />} />
      <Route path="/beta" element={<BetaSignup />} />
      <Route path="/establecimientos" element={<VenuesPage />} />
      {/* Public NPS survey — tokenised, no auth required */}
      <Route path="/nps/:token" element={<NPSPage />} />
      {/* Public deeplink invitations — `/i/<token>` */}
      <Route path="/i/:token" element={<InvitePreviewPage />} />
      {/* Viaje regalo 🎁 — `/regalo/<token>` */}
      <Route path="/regalo/:token" element={<GiftPage />} />
      
      {/* Legal Pages */}
      <Route path="/aviso-legal" element={<AvisoLegal />} />
      <Route path="/eliminar-cuenta" element={<EliminarCuenta />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidadCompleta />} />
      <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
      <Route path="/politica-cookies" element={<PoliticaCookies />} />
      {/* Legacy URLs — 301-style redirects to the consolidated pages so old
          external links keep working. */}
      <Route path="/condiciones-uso" element={<Navigate to="/terminos-condiciones" replace />} />
      <Route path="/condiciones-contratacion" element={<Navigate to="/terminos-condiciones" replace />} />
      <Route path="/politica-privacidad-completa" element={<Navigate to="/politica-privacidad" replace />} />
      <Route path="/legal/politica-privacidad" element={<Navigate to="/politica-privacidad" replace />} />
      <Route path="/legal/terminos-condiciones" element={<Navigate to="/terminos-condiciones" replace />} />
      <Route path="/legal/politica-cookies" element={<Navigate to="/politica-cookies" replace />} />
      <Route path="/legal/aviso-legal" element={<Navigate to="/aviso-legal" replace />} />
      <Route path="/trips/:tripId" element={user ? <TripDetail user={user} /> : <Navigate to="/" />} />
      <Route path="/join/:tripId" element={<JoinTrip user={user} />} />
      {/* Public storytelling page — viewable without auth */}
      <Route path="/v/:slug" element={<PublicTripStory />} />
      {/* Public flipbook preview of a saved photobook layout — no auth */}
      <Route path="/book-preview/:slug" element={<BookPreview />} />
      <Route path="/subscription" element={user ? <Subscription user={user} /> : <Navigate to="/" />} />
      <Route path="/subscription-success" element={user ? <Subscription user={user} /> : <Navigate to="/" />} />
      <Route path="/settings" element={user ? <SettingsPage user={user} setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/mi-cuenta/diagnostico" element={user ? <DiagnosticsPage /> : <Navigate to="/" />} />
      <Route path="/notifications" element={user ? <NotificationsPage user={user} setUser={setUser} /> : <Navigate to="/" />} />
      <Route path="/orders" element={user ? <PrintOrdersPage user={user} /> : <Navigate to="/" />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard user={user} />} />
      {/* Catch-all — any unregistered path (typos, dead links, bots
          probing) gets a real 404 page instead of rendering blank. */}
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </main>
      </Suspense>
      {/* First-trip-in-2-min walkthrough — only on authenticated app routes */}
      {user && /^\/(dashboard|trips|calendar|settings|notifications|subscription|orders)/.test(location.pathname) && (
        <Suspense fallback={null}>
          <OnboardingChecklist />
        </Suspense>
      )}
    </>
  );
};

function App() {
  const idleReady = useIdleReady();
  // Remote feature flags · fetch inicial. Se dispara en cuanto la SPA
  // arranca para que /trips/:id/recap tenga el flag disponible antes
  // del primer render. Coste: 1 GET pequeño sin auth (cacheable CDN).
  useEffect(() => {
    import('./utils/featureFlags').then((m) => m.loadRemoteFeatureFlags());
  }, []);
  // Remove the pre-React hero overlay (#tovias-prehero, injected by
  // index.html for LCP) once React's own hero has painted.
  useEffect(() => {
    const el = document.getElementById('tovias-prehero');
    if (!el) return;
    // Wait until the prehero image has actually decoded + painted one frame
    // before removing it — otherwise its LCP candidate never registers.
    const img = document.getElementById('tovias-prehero-img');
    const decoded = img && img.src && img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    decoded.then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.style.transition = 'opacity 250ms ease-out';
        el.style.opacity = '0';
        setTimeout(() => el.remove(), 300);
      }));
    });
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AudioPlayerProvider>
            <UtmTracker />
            <AuthHandler />
            <NativeAppBridge />
            <Suspense fallback={null}>
              <AudioPlayerBar />
            </Suspense>
            {idleReady && (
              <Suspense fallback={null}>
                <CookieBanner />
                <HomeCountryPrompt />
                <OfflineBanner />
                <OfflineOutboxBadge />
                <EmergencyFlash />
                <Toaster position="top-right" mobileOffset={{ top: 'calc(var(--safe-top) + 12px)' }} />
              </Suspense>
            )}
          </AudioPlayerProvider>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
